const SKIP_KEYS = new Set([
  'updatedAt',
  'updated_at',
  'createdAt',
  'created_at',
  'changed_at',
]);

function flatten(value, prefix = '', out = {}) {
  if (value == null || typeof value !== 'object') {
    out[prefix || '(value)'] = value;
    return out;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      out[prefix || '(empty)'] = '[]';
      return out;
    }
    const primitives = value.every((item) => item == null || typeof item !== 'object');
    if (primitives) {
      out[prefix || 'items'] = value.map((item) => (item == null ? '' : String(item))).join(', ');
      return out;
    }
    value.forEach((item, index) => {
      const path = prefix ? `${prefix}[${index}]` : `[${index}]`;
      flatten(item, path, out);
    });
    return out;
  }

  const keys = Object.keys(value);
  if (keys.length === 0) {
    out[prefix || '(empty)'] = '{}';
    return out;
  }

  keys.forEach((key) => {
    if (SKIP_KEYS.has(key)) return;
    const path = prefix ? `${prefix}.${key}` : key;
    flatten(value[key], path, out);
  });
  return out;
}

function displayValue(value) {
  if (value == null || value === '') return '—';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function humanizePath(path) {
  return String(path)
    .replace(/\[(\d+)\]/g, ' $1')
    .replace(/[._]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function valuesEqual(a, b) {
  if (a == null && b == null) return true;
  return displayValue(a) === displayValue(b);
}

/**
 * Generic field-level diff for audit JSON payloads.
 * Sales snapshots with header/items are flattened too; the report page
 * uses the dedicated sales diff when source is sales_edit.
 */
export function buildAuditDiff(oldData, newData) {
  const oldFlat = flatten(oldData == null ? {} : oldData);
  const newFlat = flatten(newData == null ? {} : newData);
  const keys = [...new Set([...Object.keys(oldFlat), ...Object.keys(newFlat)])].sort();

  const changes = keys
    .filter((key) => !valuesEqual(oldFlat[key], newFlat[key]))
    .map((key) => ({
      path: key,
      label: humanizePath(key),
      from: displayValue(oldFlat[key]),
      to: displayValue(newFlat[key]),
    }));

  return {
    changes,
    hasChanges: changes.length > 0,
    isCreate: oldData == null && newData != null,
    isDelete: oldData != null && newData == null,
  };
}

export default buildAuditDiff;
